// The CPU-side mirror of the board texture, and the packing that keeps it in step.
//
// The GPU holds the board as one RGBA16UI texture — R country, G HP, B flags, A
// spare. This module owns the staging array behind it, because the packing is the
// piece worth testing without a GL context: an off-by-one in the row stride shears
// the map by a pixel per row, which looks like a rendering bug and is arithmetic.
//
// The mirror is a full-board array on purpose, not a per-rect scratch buffer. It
// is 320x320x4 u16 = 800 KB, allocated once, and holding it whole is what lets an
// upload use UNPACK_ROW_LENGTH to read a sub-rectangle straight out of it — no
// per-rect copy, no allocation on the hot path, one texSubImage2D per dirty
// region.

import { BoardW, NumPixels } from '../wire/core';
import type { DirtyRect } from '../net/board';

/** R, G, B, A. */
export const TexelStride = 4;

/**
 * HP is a u32 on the wire and a u16 in the texture. The clamp is safe for
 * RENDERING and only for rendering: the shader turns HP into a 0..1 health ratio,
 * and a fortress pool above 65,535 is drawn full-health either way. Nothing may
 * read game state back out of this array.
 */
export const MaxTexelHP = 0xffff;

export function makeMirror(): Uint16Array {
  return new Uint16Array(NumPixels * TexelStride);
}

/**
 * Copies one rectangle of the replica into the mirror.
 *
 * Rectangles are half-open: x0/y0 inclusive, x1/y1 exclusive, matching
 * BoardReplica's dirty regions and WebGL's own sub-image arguments.
 */
export function packRect(
  mirror: Uint16Array,
  country: Uint16Array,
  hp: Uint32Array,
  flags: Uint8Array,
  r: DirtyRect,
): void {
  for (let y = r.y0; y < r.y1; y++) {
    let src = y * BoardW + r.x0;
    let dst = src * TexelStride;
    for (let x = r.x0; x < r.x1; x++) {
      const h = hp[src]!;
      mirror[dst] = country[src]!;
      mirror[dst + 1] = h > MaxTexelHP ? MaxTexelHP : h;
      mirror[dst + 2] = flags[src]!;
      mirror[dst + 3] = 0;
      src++;
      dst += TexelStride;
    }
  }
}

/** The whole board, for a keyframe. */
export function packAll(
  mirror: Uint16Array,
  country: Uint16Array,
  hp: Uint32Array,
  flags: Uint8Array,
): void {
  for (let i = 0; i < NumPixels; i++) {
    const h = hp[i]!;
    const at = i * TexelStride;
    mirror[at] = country[i]!;
    mirror[at + 1] = h > MaxTexelHP ? MaxTexelHP : h;
    mirror[at + 2] = flags[i]!;
    mirror[at + 3] = 0;
  }
}

/** The area of a rectangle, in texels. Used to decide when many small rects are worse than one big one. */
export function rectArea(r: DirtyRect): number {
  return Math.max(0, r.x1 - r.x0) * Math.max(0, r.y1 - r.y0);
}

/**
 * Merges dirty rectangles when uploading them separately would cost more than
 * uploading their bounding box.
 *
 * Each texSubImage2D is a driver call with fixed overhead measured in the low
 * microseconds, so a tick that dirties thirty chunks by one pixel each is better
 * served by one upload of their bounding box than by thirty of one texel. The
 * threshold is deliberately generous: bandwidth to the GPU for the WHOLE board is
 * 800 KB, which is nothing, and the thing actually worth avoiding is the call
 * count.
 */
export function planUploads(rects: readonly DirtyRect[], maxCalls = 8): DirtyRect[] {
  if (rects.length <= maxCalls) return rects.slice();
  let x0 = Infinity;
  let y0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  for (const r of rects) {
    if (r.x0 < x0) x0 = r.x0;
    if (r.y0 < y0) y0 = r.y0;
    if (r.x1 > x1) x1 = r.x1;
    if (r.y1 > y1) y1 = r.y1;
  }
  if (!Number.isFinite(x0)) return [];
  return [{ x0, y0, x1, y1 }];
}
